import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { MobileArrowLeft } from '@agendapro/emerald-icons';
import { useRouter } from 'next/router';

import { Icon, InitialsAvatar, Paragraph } from '../../UI';
import { ScheduleMap } from './ScheduleMap/ScheduleMap';
import { ProviderDetailsProps } from './ProviderDetails.types';
import { useIframeRouter, usePageContext } from '@/hooks';
import * as St from './ProviderDetails.styles';
import { useServiceProviders } from '@/services/serviceProviders';

export const ProviderDetails: FC<ProviderDetailsProps> = ({
  imageUrl,
  name,
  schedule,
  className,
  withoutReturn,
  biography,
}) => {
  const { t } = useTranslation();
  const { companyOverview, currentLocation, queryParams } = usePageContext();
  const { routerPush } = useIframeRouter();
  const router = useRouter();
  const { countryCode } = router.query as { countryCode: string };
  const theme = useTheme();

  const { data: serviceProviders } = useServiceProviders(currentLocation?.id?.toString() || '');
  const containerRef = useRef<HTMLDivElement>(null);
  const [clamped, setClamped] = useState(true);
  const [showButton, setShowButton] = useState(false);
  const handleClick = () => setClamped(!clamped);

  const goToBranchLocation = () => {
    routerPush(`/${countryCode}/sucursal/${currentLocation?.id}/${queryParams}`);
  };

  useEffect(() => {
    if (containerRef?.current?.scrollHeight !== containerRef?.current?.clientHeight) {
      setShowButton(true);
    }
  }, [containerRef?.current?.scrollHeight]);

  if (withoutReturn) {
    return (
      <>
        <St.ProviderDetailsContainer className={className}>
          <St.RightBox>
            {imageUrl ? (
              <St.ProviderPortraitContainer>
                <St.ProviderPortrait src={imageUrl} height={80} width={80} />
              </St.ProviderPortraitContainer>
            ) : (
              <InitialsAvatar name={name} />
            )}
            <St.DataContainer>
              <Paragraph size="subHeadline" weight="bold" className="name">
                {name}
              </Paragraph>
              {schedule && <ScheduleMap schedule={schedule} />}
            </St.DataContainer>
          </St.RightBox>
        </St.ProviderDetailsContainer>
      </>
    );
  }

  return companyOverview?.isPlanSolo ? null : (
    <St.ProviderDetailsContainer className={className}>
      {serviceProviders && serviceProviders?.length > 1 && (
        <St.LeftBox>
          <Icon
            onClick={goToBranchLocation}
            className="backIcon"
            icon={<MobileArrowLeft />}
            color={theme.brandTheme.brand100}
            size="small"
          />
        </St.LeftBox>
      )}
      <St.RightBox>
        {imageUrl ? (
          <St.ProviderPortraitContainer>
            <St.ProviderPortrait src={imageUrl} height={67} width={67} />
          </St.ProviderPortraitContainer>
        ) : (
          <St.CustomInitialsAvatar name={name} />
        )}
        <St.DataContainer>
          <Paragraph size="subHeadline" weight="bold" className="name">
            {name}
          </Paragraph>
          {biography && (
            <div
              className={`relative pt-2 mb-4 md:pt-4 pb-0 ${
                clamped ? 'max-h-[46px] md:max-h-[unset]' : 'max-h-[unset]'
              }`}
            >
              <div
                ref={containerRef}
                className={`overflow-hidden overflow-wrap break-word ${
                  clamped ? 'max-h-[38px] xl:max-h-[76px]' : 'max-h-[unset]'
                }`}
              >
                <Paragraph size="small" weight="light">
                  {biography}
                  {showButton && !clamped && biography && (
                    <button type="button" onClick={handleClick} data-testid="read-less-bio">
                      <p className="font-semibold underline text-text">{t('READ_LESS')}</p>
                    </button>
                  )}
                </Paragraph>
              </div>
              {showButton && clamped && biography && (
                <Paragraph size="small" weight="light">
                  <button
                    type="button"
                    onClick={handleClick}
                    className="absolute bottom-0 right-0 bg-white flex flex-row items-center gap-1"
                    data-testid="read-more-bio"
                  >
                    ...
                    <p className="font-semibold underline text-text">{t('READ_MORE')}</p>
                  </button>
                </Paragraph>
              )}
            </div>
          )}
          {schedule && <ScheduleMap schedule={schedule} />}
          {serviceProviders && serviceProviders?.length > 1 && (
            <St.StyledLink onClick={goToBranchLocation}>{t('SHOW_BRANCH_LOCATION')}</St.StyledLink>
          )}
        </St.DataContainer>
      </St.RightBox>
    </St.ProviderDetailsContainer>
  );
};
